const TRAILING_SLASH_REGEX = /\/+$/

export const normalizePath = value => {
  if (!value || typeof value !== 'string') {
    return '/'
  }

  const [withoutHash] = value.split('#')
  const [withoutQuery] = withoutHash.split('?')
  const sanitized = withoutQuery.trim()

  if (!sanitized) {
    return '/'
  }

  const withLeadingSlash = sanitized.startsWith('/') ? sanitized : `/${sanitized}`

  if (withLeadingSlash === '/') {
    return '/'
  }

  return withLeadingSlash.replace(TRAILING_SLASH_REGEX, '') || '/'
}

const isInternalUrl = url => typeof url === 'string' && url.startsWith('/')

const getPathSegments = path => normalizePath(path).split('/').filter(Boolean)

const isSegmentPrefix = (candidatePath, currentPath) => {
  const candidateSegments = getPathSegments(candidatePath)
  const currentSegments = getPathSegments(currentPath)

  if (!candidateSegments.length || candidateSegments.length > currentSegments.length) {
    return false
  }

  return candidateSegments.every((segment, index) => currentSegments[index] === segment)
}

export const flattenMenuItems = (items = [], parentKeys = []) => {
  if (!Array.isArray(items)) {
    return []
  }

  return items.reduce((acc, item) => {
    if (!item || !item.key) {
      return acc
    }

    const current = {
      key: item.key,
      url: isInternalUrl(item.url) ? normalizePath(item.url) : null,
      parentKeys,
      hasChildren: Array.isArray(item.children) && item.children.length > 0,
    }

    acc.push(current)

    if (current.hasChildren) {
      acc.push(...flattenMenuItems(item.children, [...parentKeys, item.key]))
    }

    return acc
  }, [])
}

const scoreByRoutePrefix = (menuPath, currentPath) => {
  if (!menuPath || !currentPath) {
    return -1
  }

  if (isSegmentPrefix(menuPath, currentPath)) {
    return menuPath.length + 1000
  }

  const menuSegments = getPathSegments(menuPath)
  const currentSegments = getPathSegments(currentPath)

  if (!menuSegments.length || !currentSegments.length) {
    return -1
  }

  let commonSegments = 0
  const maxComparisons = Math.min(menuSegments.length, currentSegments.length)

  while (commonSegments < maxComparisons && menuSegments[commonSegments] === currentSegments[commonSegments]) {
    commonSegments += 1
  }

  if (commonSegments === 0) {
    return -1
  }

  return commonSegments * 10 - Math.abs(menuSegments.length - currentSegments.length)
}

export const resolveMenuSelection = ({ menuData = [], pathname = '/', routeKeyMenu = '' } = {}) => {
  const flatItems = flattenMenuItems(menuData)
  const currentPath = normalizePath(pathname)

  if (!flatItems.length) {
    return {
      selectedKey: null,
      ancestorKeys: [],
    }
  }

  if (routeKeyMenu) {
    const byRouteKey = flatItems.find(item => item.key === routeKeyMenu)
    if (byRouteKey) {
      return {
        selectedKey: byRouteKey.key,
        ancestorKeys: byRouteKey.parentKeys,
      }
    }
  }

  const exactMatch = flatItems.find(item => item.url && item.url === currentPath)

  if (exactMatch) {
    return {
      selectedKey: exactMatch.key,
      ancestorKeys: exactMatch.parentKeys,
    }
  }

  const bestScoredItem = flatItems
    .filter(item => item.url)
    .map(item => ({
      item,
      score: scoreByRoutePrefix(item.url, currentPath),
    }))
    .filter(candidate => candidate.score >= 0)
    .sort((a, b) => b.score - a.score)[0]

  if (!bestScoredItem) {
    return {
      selectedKey: null,
      ancestorKeys: [],
    }
  }

  return {
    selectedKey: bestScoredItem.item.key,
    ancestorKeys: bestScoredItem.item.parentKeys,
  }
}

export const mergeOpenKeys = (...groups) => {
  const merged = groups.flat().filter(Boolean)
  return Array.from(new Set(merged))
}
